import { FormEvent, ReactNode, useMemo, useState } from 'react';
import {
  ArrowUpIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DraggableDetailPanel } from '../../components/ui/DraggableDetailPanel';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import {
  PortalChangeItem,
  PortalChangeSituation,
  PortalChangeType,
  PortalRiskItem,
  PortalRiskLevel,
  PortalRiskSituation,
  PortalRisksChangesData,
} from './portalRisksChangesMock';

type SectionView = 'risks' | 'changes';
type RiskDetailTab = 'details' | 'plan' | 'history';
type ChangeDetailTab = 'details' | 'impact' | 'approvals' | 'history';

const riskLevelLabel: Record<PortalRiskLevel, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const riskLevelClass: Record<PortalRiskLevel, string> = {
  high: 'bg-red-50 text-red-700',
  medium: 'bg-orange-50 text-orange-700',
  low: 'bg-emerald-50 text-emerald-700',
};

const riskSituationLabel: Record<PortalRiskSituation, string> = {
  treatment: 'Em tratamento',
  monitoring: 'Em monitoramento',
  controlled: 'Controlado',
};

const riskSituationClass: Record<PortalRiskSituation, string> = {
  treatment: 'bg-orange-50 text-orange-700',
  monitoring: 'bg-primary-50 text-primary-700',
  controlled: 'bg-emerald-50 text-emerald-700',
};

const riskDotClass: Record<PortalRiskSituation, string> = {
  treatment: 'bg-orange-500',
  monitoring: 'bg-primary-500',
  controlled: 'bg-emerald-500',
};

const changeTypeLabel: Record<PortalChangeType, string> = {
  improvement: 'Melhoria',
  scope: 'Escopo',
  fix: 'Correção',
};

const changeTypeClass: Record<PortalChangeType, string> = {
  improvement: 'bg-violet-50 text-violet-700',
  scope: 'bg-red-50 text-red-700',
  fix: 'bg-rose-50 text-rose-700',
};

const changeSituationLabel: Record<PortalChangeSituation, string> = {
  analysis: 'Em análise',
  awaitingApproval: 'Aguardando aprovação',
  approved: 'Aprovada',
  implemented: 'Implementada',
};

const changeSituationClass: Record<PortalChangeSituation, string> = {
  analysis: 'bg-primary-50 text-primary-700',
  awaitingApproval: 'bg-orange-50 text-orange-700',
  approved: 'bg-emerald-50 text-emerald-700',
  implemented: 'bg-emerald-50 text-emerald-700',
};

const riskLevelOptions: { id: PortalRiskLevel; label: string }[] = [
  { id: 'high', label: 'Alta' },
  { id: 'medium', label: 'Média' },
  { id: 'low', label: 'Baixa' },
];

const riskSituationOptions: { id: PortalRiskSituation; label: string }[] = [
  { id: 'treatment', label: 'Em tratamento' },
  { id: 'monitoring', label: 'Em monitoramento' },
  { id: 'controlled', label: 'Controlado' },
];

const riskOwnerOptions = ['Rafael Oliveira', 'Ana Souza', 'Carlos Mendes', 'Mariana Silva'];

const changeTypeOptions: { id: PortalChangeType; label: string }[] = [
  { id: 'improvement', label: 'Melhoria' },
  { id: 'scope', label: 'Escopo' },
  { id: 'fix', label: 'Correção' },
];

const changePriorityOptions = ['Alta', 'Média', 'Baixa'];

const changeOwnerOptions = ['Rafael Oliveira', 'Ana Souza', 'Carlos Mendes', 'Mariana Silva', 'Bruno Costa'];

type NewRiskFormState = {
  title: string;
  description: string;
  probability: PortalRiskLevel;
  impact: PortalRiskLevel;
  situation: PortalRiskSituation;
  ownerName: string;
  cause: string;
  responsePlan: string;
};

const emptyNewRiskForm: NewRiskFormState = {
  title: '',
  description: '',
  probability: 'medium',
  impact: 'medium',
  situation: 'monitoring',
  ownerName: riskOwnerOptions[0],
  cause: '',
  responsePlan: '',
};

type NewChangeFormState = {
  title: string;
  description: string;
  type: PortalChangeType;
  priorityLabel: string;
  ownerName: string;
  justification: string;
  impactSchedule: string;
  impactScope: string;
  impactCost: string;
};

const emptyNewChangeForm: NewChangeFormState = {
  title: '',
  description: '',
  type: 'improvement',
  priorityLabel: 'Média',
  ownerName: changeOwnerOptions[0],
  justification: '',
  impactSchedule: '',
  impactScope: '',
  impactCost: '',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type PortalProjectRisksChangesProps = {
  data: PortalRisksChangesData;
};

export function PortalProjectRisksChanges({ data }: PortalProjectRisksChangesProps) {
  const [section, setSection] = useState<SectionView>('changes');
  const [risks, setRisks] = useState<PortalRiskItem[]>(data.risks);
  const [changes, setChanges] = useState<PortalChangeItem[]>(data.changes);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(data.risks[0]?.id ?? null);
  const [selectedChangeId, setSelectedChangeId] = useState<string | null>('chg-015');
  const [riskTab, setRiskTab] = useState<RiskDetailTab>('details');
  const [changeTab, setChangeTab] = useState<ChangeDetailTab>('details');
  const [approvedChangeIds, setApprovedChangeIds] = useState<string[]>([]);
  const [adjustmentsModalOpen, setAdjustmentsModalOpen] = useState(false);
  const [adjustmentMessage, setAdjustmentMessage] = useState('');
  const [adjustmentSentFor, setAdjustmentSentFor] = useState<string[]>([]);
  const [newRiskModalOpen, setNewRiskModalOpen] = useState(false);
  const [newRiskForm, setNewRiskForm] = useState<NewRiskFormState>(emptyNewRiskForm);
  const [newChangeModalOpen, setNewChangeModalOpen] = useState(false);
  const [newChangeForm, setNewChangeForm] = useState<NewChangeFormState>(emptyNewChangeForm);

  const selectedRisk = useMemo(
    () => risks.find((item) => item.id === selectedRiskId) ?? null,
    [risks, selectedRiskId],
  );
  const selectedChange = useMemo(() => {
    const item = changes.find((entry) => entry.id === selectedChangeId) ?? null;
    if (!item) return null;
    if (approvedChangeIds.includes(item.id)) {
      return { ...item, situation: 'approved' as const };
    }
    return item;
  }, [approvedChangeIds, changes, selectedChangeId]);

  const risksInAttention = useMemo(
    () => risks.filter((item) => item.situation === 'treatment' || item.probability === 'high').length,
    [risks],
  );

  const changesInAnalysis = useMemo(
    () =>
      changes.filter((item) => {
        const situation = approvedChangeIds.includes(item.id) ? 'approved' : item.situation;
        return situation === 'analysis' || situation === 'awaitingApproval';
      }).length,
    [approvedChangeIds, changes],
  );

  const changesApproved = useMemo(
    () =>
      changes.filter((item) => {
        const situation = approvedChangeIds.includes(item.id) ? 'approved' : item.situation;
        return situation === 'approved' || situation === 'implemented';
      }).length,
    [approvedChangeIds, changes],
  );

  function openAdjustmentsModal() {
    setAdjustmentMessage('');
    setAdjustmentsModalOpen(true);
  }

  function submitAdjustments(event: FormEvent) {
    event.preventDefault();
    if (!selectedChange || !adjustmentMessage.trim()) return;
    setAdjustmentSentFor((current) =>
      current.includes(selectedChange.id) ? current : [...current, selectedChange.id],
    );
    setAdjustmentsModalOpen(false);
    setAdjustmentMessage('');
  }

  function openNewRiskModal() {
    setNewRiskForm(emptyNewRiskForm);
    setNewRiskModalOpen(true);
  }

  function updateNewRiskForm<K extends keyof NewRiskFormState>(key: K, value: NewRiskFormState[K]) {
    setNewRiskForm((current) => ({ ...current, [key]: value }));
  }

  function submitNewRisk(event: FormEvent) {
    event.preventDefault();
    if (!newRiskForm.title.trim() || !newRiskForm.description.trim()) return;

    const next: PortalRiskItem = {
      id: `risk-${Date.now()}`,
      title: newRiskForm.title.trim(),
      description: newRiskForm.description.trim(),
      probability: newRiskForm.probability,
      impact: newRiskForm.impact,
      situation: newRiskForm.situation,
      ownerName: newRiskForm.ownerName,
      cause: newRiskForm.cause.trim() || 'A definir',
      responsePlan: newRiskForm.responsePlan.trim() || 'Plano de resposta a definir.',
      impactSchedule: 'A avaliar',
      impactScope: 'A avaliar',
      impactCost: 'Não estimado',
      history: [
        {
          id: `rh-new-${Date.now()}`,
          title: 'Risco registrado',
          detail: 'Risco criado pelo portal do cliente',
          whenLabel: new Date().toLocaleDateString('pt-BR'),
        },
      ],
    };

    setRisks((current) => [next, ...current]);
    setSelectedRiskId(next.id);
    setRiskTab('details');
    setSection('risks');
    setNewRiskModalOpen(false);
    setNewRiskForm(emptyNewRiskForm);
  }

  function openNewChangeModal() {
    setNewChangeForm(emptyNewChangeForm);
    setNewChangeModalOpen(true);
  }

  function updateNewChangeForm<K extends keyof NewChangeFormState>(key: K, value: NewChangeFormState[K]) {
    setNewChangeForm((current) => ({ ...current, [key]: value }));
  }

  function nextChangeNumber(): string {
    const maxNumber = changes.reduce((max, item) => {
      const parsed = Number.parseInt(item.number, 10);
      return Number.isFinite(parsed) ? Math.max(max, parsed) : max;
    }, 0);
    return String(maxNumber + 1).padStart(3, '0');
  }

  function submitNewChange(event: FormEvent) {
    event.preventDefault();
    if (!newChangeForm.title.trim() || !newChangeForm.description.trim() || !newChangeForm.justification.trim()) {
      return;
    }

    const number = nextChangeNumber();
    const todayLabel = new Date().toLocaleDateString('pt-BR');
    const next: PortalChangeItem = {
      id: `chg-${number}-${Date.now()}`,
      number,
      title: newChangeForm.title.trim(),
      description: newChangeForm.description.trim(),
      type: newChangeForm.type,
      priorityLabel: newChangeForm.priorityLabel,
      situation: 'analysis',
      dateLabel: todayLabel,
      requestedBy: 'Ana Souza',
      ownerName: newChangeForm.ownerName,
      impactSchedule: newChangeForm.impactSchedule.trim() || 'A avaliar',
      impactScope: newChangeForm.impactScope.trim() || 'A avaliar',
      impactCost: newChangeForm.impactCost.trim() || 'Não estimado',
      justification: newChangeForm.justification.trim(),
      approvals: [
        { id: `ap-${Date.now()}`, name: 'João Santos', role: 'Patrocinador', statusLabel: 'Pendente' },
      ],
      history: [
        {
          id: `ch-new-${Date.now()}`,
          title: 'Solicitação aberta',
          detail: 'Registrada por Ana Souza',
          whenLabel: todayLabel,
        },
      ],
    };

    setChanges((current) => [next, ...current]);
    setSelectedChangeId(next.id);
    setChangeTab('details');
    setSection('changes');
    setNewChangeModalOpen(false);
    setNewChangeForm(emptyNewChangeForm);
  }

  return (
    <div className={`relative space-y-5 ${selectedRisk || selectedChange ? 'xl:pr-[24rem]' : ''}`}>
      <div>
        <h2 className="text-xl font-bold text-secondary-900">Riscos e Mudanças</h2>
        <p className="mt-1 max-w-2xl text-sm text-secondary-600">
          Acompanhe riscos compartilhados e solicitações de mudança do projeto.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard
          label="Riscos em atenção"
          value={risksInAttention}
          icon={<ExclamationTriangleIcon className="h-5 w-5" aria-hidden />}
          tone="red"
        />
        <SummaryCard
          label="Mudanças em análise"
          value={changesInAnalysis}
          icon={<ClockIcon className="h-5 w-5" aria-hidden />}
          tone="amber"
        />
        <SummaryCard
          label="Mudanças aprovadas"
          value={changesApproved}
          icon={<CheckIcon className="h-5 w-5" aria-hidden />}
          tone="green"
        />
      </div>

      <div className="inline-flex rounded-xl border border-secondary-200 bg-white p-1">
        <button
          type="button"
          onClick={() => setSection('risks')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            section === 'risks' ? 'bg-primary-500 text-white' : 'text-secondary-700 hover:bg-secondary-50'
          }`}
        >
          Riscos
        </button>
        <button
          type="button"
          onClick={() => setSection('changes')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            section === 'changes' ? 'bg-primary-500 text-white' : 'text-secondary-700 hover:bg-secondary-50'
          }`}
        >
          Mudanças
        </button>
      </div>

      {section === 'risks' ? (
        <section className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
            <h3 className="text-base font-semibold text-secondary-900">Riscos do projeto</h3>
            <button
              type="button"
              onClick={openNewRiskModal}
              className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
            >
              <PlusIcon className="h-4 w-4" aria-hidden />
              Novo risco
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Risco</th>
                  <th className="px-3 py-3 font-medium">Probabilidade</th>
                  <th className="px-3 py-3 font-medium">Impacto</th>
                  <th className="px-3 py-3 font-medium">Situação</th>
                  <th className="px-3 py-3 font-medium">Responsável</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((risk) => (
                  <tr
                    key={risk.id}
                    onClick={() => {
                      setSelectedRiskId(risk.id);
                      setRiskTab('details');
                    }}
                    className={`cursor-pointer border-t border-secondary-100 ${
                      selectedRiskId === risk.id ? 'bg-primary-50/70' : 'hover:bg-secondary-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${riskDotClass[risk.situation]}`} />
                        <div>
                          <p className="font-semibold text-secondary-900">{risk.title}</p>
                          <p className="mt-0.5 line-clamp-1 text-xs text-secondary-500">{risk.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={riskLevelClass[risk.probability]}>{riskLevelLabel[risk.probability]}</Tag>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={riskLevelClass[risk.impact]}>{riskLevelLabel[risk.impact]}</Tag>
                    </td>
                    <td className="px-3 py-3">
                      <Tag className={riskSituationClass[risk.situation]}>{riskSituationLabel[risk.situation]}</Tag>
                    </td>
                    <td className="px-3 py-3">
                      <OwnerCell name={risk.ownerName} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
            <h3 className="text-base font-semibold text-secondary-900">Solicitações de mudança</h3>
            <button
              type="button"
              onClick={openNewChangeModal}
              className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
            >
              <PlusIcon className="h-4 w-4" aria-hidden />
              Nova solicitação
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-3 py-3 font-medium">Solicitação</th>
                  <th className="px-3 py-3 font-medium">Tipo</th>
                  <th className="px-3 py-3 font-medium">Impacto</th>
                  <th className="px-3 py-3 font-medium">Situação</th>
                  <th className="px-3 py-3 font-medium">Data</th>
                  <th className="px-3 py-3 font-medium">Responsável</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change) => {
                  const situation = approvedChangeIds.includes(change.id) ? 'approved' : change.situation;
                  return (
                    <tr
                      key={change.id}
                      onClick={() => {
                        setSelectedChangeId(change.id);
                        setChangeTab('details');
                      }}
                      className={`cursor-pointer border-t border-secondary-100 ${
                        selectedChangeId === change.id ? 'bg-primary-50/70' : 'hover:bg-secondary-50'
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-secondary-500">{change.number}</td>
                      <td className="max-w-xs px-3 py-3">
                        <p className="font-semibold text-secondary-900">{change.title}</p>
                        <p className="mt-0.5 line-clamp-1 text-xs text-secondary-500">{change.description}</p>
                      </td>
                      <td className="px-3 py-3">
                        <Tag className={changeTypeClass[change.type]}>{changeTypeLabel[change.type]}</Tag>
                      </td>
                      <td className="px-3 py-3 text-xs text-secondary-600">
                        Prazo: {change.impactSchedule}
                        <br />
                        Custo: {change.impactCost}
                      </td>
                      <td className="px-3 py-3">
                        <Tag className={changeSituationClass[situation]}>{changeSituationLabel[situation]}</Tag>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{change.dateLabel}</td>
                      <td className="px-3 py-3">
                        <OwnerCell name={change.ownerName} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {section === 'risks' && selectedRisk ? (
        <RiskDetailDrawer
          risk={selectedRisk}
          activeTab={riskTab}
          onTabChange={setRiskTab}
          onClose={() => setSelectedRiskId(null)}
        />
      ) : null}

      {section === 'changes' && selectedChange ? (
        <ChangeDetailDrawer
          change={selectedChange}
          activeTab={changeTab}
          onTabChange={setChangeTab}
          onClose={() => setSelectedChangeId(null)}
          adjustmentSent={adjustmentSentFor.includes(selectedChange.id)}
          onApprove={() =>
            setApprovedChangeIds((current) =>
              current.includes(selectedChange.id) ? current : [...current, selectedChange.id],
            )
          }
          onRequestAdjustments={openAdjustmentsModal}
        />
      ) : null}

      <DraggableModalPanel
        open={adjustmentsModalOpen}
        title="Solicitar ajustes"
        subtitle={
          selectedChange
            ? `#${selectedChange.number} — ${selectedChange.title}`
            : 'Descreva os ajustes necessários'
        }
        onClose={() => setAdjustmentsModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setAdjustmentsModalOpen(false)}>
            <button
              type="submit"
              form="portal-change-adjustments-form"
              className="erp-btn-primary"
              disabled={!adjustmentMessage.trim()}
            >
              Enviar solicitação
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-change-adjustments-form" onSubmit={submitAdjustments} className="space-y-3">
          <label className="erp-label" htmlFor="portal-change-adjustment-message">
            Mensagem
          </label>
          <textarea
            id="portal-change-adjustment-message"
            value={adjustmentMessage}
            onChange={(event) => setAdjustmentMessage(event.target.value)}
            rows={5}
            required
            placeholder="Descreva os ajustes necessários para esta mudança..."
            className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
          <p className="text-xs text-secondary-500">
            A equipe Baruk receberá sua mensagem e manterá o histórico da solicitação.
          </p>
        </form>
      </DraggableModalPanel>

      <DraggableModalPanel
        open={newRiskModalOpen}
        title="Novo risco"
        subtitle="Registre um risco compartilhado do projeto."
        onClose={() => setNewRiskModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setNewRiskModalOpen(false)}>
            <button
              type="submit"
              form="portal-new-risk-form"
              className="erp-btn-primary"
              disabled={!newRiskForm.title.trim() || !newRiskForm.description.trim()}
            >
              Criar risco
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-new-risk-form" onSubmit={submitNewRisk} className="space-y-4">
          <label className="block">
            <span className="erp-label">Título</span>
            <input
              value={newRiskForm.title}
              onChange={(event) => updateNewRiskForm('title', event.target.value)}
              required
              placeholder="Ex.: Atraso na liberação de acessos"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              value={newRiskForm.description}
              onChange={(event) => updateNewRiskForm('description', event.target.value)}
              required
              rows={3}
              placeholder="Descreva o risco e o possível impacto no projeto..."
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Probabilidade</span>
              <select
                value={newRiskForm.probability}
                onChange={(event) => updateNewRiskForm('probability', event.target.value as PortalRiskLevel)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {riskLevelOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Impacto</span>
              <select
                value={newRiskForm.impact}
                onChange={(event) => updateNewRiskForm('impact', event.target.value as PortalRiskLevel)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {riskLevelOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Situação</span>
              <select
                value={newRiskForm.situation}
                onChange={(event) => updateNewRiskForm('situation', event.target.value as PortalRiskSituation)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {riskSituationOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Responsável</span>
              <select
                value={newRiskForm.ownerName}
                onChange={(event) => updateNewRiskForm('ownerName', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {riskOwnerOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="erp-label">Causa</span>
            <textarea
              value={newRiskForm.cause}
              onChange={(event) => updateNewRiskForm('cause', event.target.value)}
              rows={2}
              placeholder="O que pode originar este risco?"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <label className="block">
            <span className="erp-label">Plano de resposta</span>
            <textarea
              value={newRiskForm.responsePlan}
              onChange={(event) => updateNewRiskForm('responsePlan', event.target.value)}
              rows={3}
              placeholder="Quais ações serão tomadas para mitigar ou tratar o risco?"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
        </form>
      </DraggableModalPanel>

      <DraggableModalPanel
        open={newChangeModalOpen}
        title="Nova solicitação"
        subtitle="Registre uma solicitação de mudança do projeto."
        onClose={() => setNewChangeModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setNewChangeModalOpen(false)}>
            <button
              type="submit"
              form="portal-new-change-form"
              className="erp-btn-primary"
              disabled={
                !newChangeForm.title.trim() ||
                !newChangeForm.description.trim() ||
                !newChangeForm.justification.trim()
              }
            >
              Criar solicitação
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-new-change-form" onSubmit={submitNewChange} className="space-y-4">
          <label className="block">
            <span className="erp-label">Título</span>
            <input
              value={newChangeForm.title}
              onChange={(event) => updateNewChangeForm('title', event.target.value)}
              required
              placeholder="Ex.: Incluir novo indicador no dashboard"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              value={newChangeForm.description}
              onChange={(event) => updateNewChangeForm('description', event.target.value)}
              required
              rows={3}
              placeholder="Descreva a mudança solicitada..."
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Tipo</span>
              <select
                value={newChangeForm.type}
                onChange={(event) => updateNewChangeForm('type', event.target.value as PortalChangeType)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {changeTypeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="erp-label">Prioridade</span>
              <select
                value={newChangeForm.priorityLabel}
                onChange={(event) => updateNewChangeForm('priorityLabel', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {changePriorityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="erp-label">Responsável</span>
              <select
                value={newChangeForm.ownerName}
                onChange={(event) => updateNewChangeForm('ownerName', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {changeOwnerOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="erp-label">Justificativa</span>
            <textarea
              value={newChangeForm.justification}
              onChange={(event) => updateNewChangeForm('justification', event.target.value)}
              required
              rows={3}
              placeholder="Por que esta mudança é necessária?"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="erp-label">Impacto no prazo</span>
              <input
                value={newChangeForm.impactSchedule}
                onChange={(event) => updateNewChangeForm('impactSchedule', event.target.value)}
                placeholder="Ex.: + 5 dias"
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </label>
            <label className="block">
              <span className="erp-label">Impacto no escopo</span>
              <input
                value={newChangeForm.impactScope}
                onChange={(event) => updateNewChangeForm('impactScope', event.target.value)}
                placeholder="Ex.: Dashboard"
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </label>
            <label className="block">
              <span className="erp-label">Impacto no custo</span>
              <input
                value={newChangeForm.impactCost}
                onChange={(event) => updateNewChangeForm('impactCost', event.target.value)}
                placeholder="Ex.: R$ 8.000"
                className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </label>
          </div>
        </form>
      </DraggableModalPanel>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  tone: 'red' | 'amber' | 'green';
}) {
  const toneClass = {
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-emerald-50 text-emerald-700',
  }[tone];

  return (
    <article className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-secondary-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-secondary-900">{value}</p>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneClass}`}>{icon}</span>
      </div>
    </article>
  );
}

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>{children}</span>
  );
}

function OwnerCell({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
        {initials(name)}
      </span>
      <span className="whitespace-nowrap text-secondary-700">{name}</span>
    </span>
  );
}

function RiskDetailDrawer({
  risk,
  activeTab,
  onTabChange,
  onClose,
}: {
  risk: PortalRiskItem;
  activeTab: RiskDetailTab;
  onTabChange: (tab: RiskDetailTab) => void;
  onClose: () => void;
}) {
  return (
    <DraggableDetailPanel
      resetKey={risk.id}
      header={<DrawerHeader title={risk.title} onClose={onClose} />}
    >
      <DrawerTabs
        tabs={[
          ['details', 'Detalhes'],
          ['plan', 'Plano de resposta'],
          ['history', 'Histórico'],
        ]}
        active={activeTab}
        onChange={(tab) => onTabChange(tab as RiskDetailTab)}
      />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'details' ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-secondary-700">{risk.description}</p>
            <div className="grid grid-cols-2 gap-3">
              <Meta label="Probabilidade">
                <Tag className={riskLevelClass[risk.probability]}>{riskLevelLabel[risk.probability]}</Tag>
              </Meta>
              <Meta label="Impacto">
                <Tag className={riskLevelClass[risk.impact]}>{riskLevelLabel[risk.impact]}</Tag>
              </Meta>
              <Meta label="Situação">
                <Tag className={riskSituationClass[risk.situation]}>{riskSituationLabel[risk.situation]}</Tag>
              </Meta>
              <Meta label="Responsável">
                <OwnerCell name={risk.ownerName} />
              </Meta>
            </div>
            <Meta label="Causa">
              <p className="text-sm text-secondary-700">{risk.cause}</p>
            </Meta>
            <ImpactBoxes
              schedule={risk.impactSchedule}
              scope={risk.impactScope}
              cost={risk.impactCost}
            />
          </div>
        ) : null}
        {activeTab === 'plan' ? (
          <p className="text-sm leading-relaxed text-secondary-700">{risk.responsePlan}</p>
        ) : null}
        {activeTab === 'history' ? <HistoryList items={risk.history} /> : null}
      </div>
    </DraggableDetailPanel>
  );
}

function ChangeDetailDrawer({
  change,
  activeTab,
  onTabChange,
  onClose,
  adjustmentSent,
  onApprove,
  onRequestAdjustments,
}: {
  change: PortalChangeItem;
  activeTab: ChangeDetailTab;
  onTabChange: (tab: ChangeDetailTab) => void;
  onClose: () => void;
  adjustmentSent: boolean;
  onApprove: () => void;
  onRequestAdjustments: () => void;
}) {
  const canAct = change.situation === 'analysis' || change.situation === 'awaitingApproval';

  return (
    <DraggableDetailPanel
      resetKey={change.id}
      header={
        <DrawerHeader
          title={`#${change.number} — ${change.title}`}
          badge={<Tag className={changeSituationClass[change.situation]}>{changeSituationLabel[change.situation]}</Tag>}
          onClose={onClose}
        />
      }
      footer={
        canAct && activeTab === 'details' ? (
          <div className="flex items-center gap-2 p-4">
            <button type="button" onClick={onApprove} className="erp-btn-primary flex flex-1 items-center justify-center gap-2">
              <CheckIcon className="h-4 w-4" aria-hidden />
              Aprovar mudança
            </button>
            <button
              type="button"
              onClick={onRequestAdjustments}
              className="erp-btn-secondary flex flex-1 items-center justify-center gap-2"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
              Solicitar ajustes
            </button>
          </div>
        ) : undefined
      }
    >
      <DrawerTabs
        tabs={[
          ['details', 'Detalhes'],
          ['impact', 'Impacto'],
          ['approvals', 'Aprovações'],
          ['history', 'Histórico'],
        ]}
        active={activeTab}
        onChange={(tab) => onTabChange(tab as ChangeDetailTab)}
      />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'details' ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-secondary-700">{change.description}</p>
            {adjustmentSent ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Ajustes solicitados. A equipe Baruk foi notificada.
              </p>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <Meta label="Tipo">
                <Tag className={changeTypeClass[change.type]}>{changeTypeLabel[change.type]}</Tag>
              </Meta>
              <Meta label="Prioridade">
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-700">
                  <ArrowUpIcon className="h-3.5 w-3.5" aria-hidden />
                  {change.priorityLabel}
                </span>
              </Meta>
              <Meta label="Solicitada por">
                <OwnerCell name={change.requestedBy} />
              </Meta>
              <Meta label="Data">
                <span className="text-sm font-semibold text-secondary-900">{change.dateLabel}</span>
              </Meta>
            </div>
            <Meta label="Justificativa">
              <p className="text-sm text-secondary-700">{change.justification}</p>
            </Meta>
            <ImpactBoxes
              schedule={change.impactSchedule}
              scope={change.impactScope}
              cost={change.impactCost}
            />
          </div>
        ) : null}
        {activeTab === 'impact' ? (
          <ImpactBoxes
            schedule={change.impactSchedule}
            scope={change.impactScope}
            cost={change.impactCost}
          />
        ) : null}
        {activeTab === 'approvals' ? (
          <ul className="space-y-3">
            {change.approvals.length === 0 ? (
              <li className="text-sm text-secondary-500">Nenhum aprovador registrado.</li>
            ) : (
              change.approvals.map((approval) => (
                <li key={approval.id} className="rounded-xl border border-secondary-200 px-3 py-2.5">
                  <p className="text-sm font-semibold text-secondary-900">{approval.name}</p>
                  <p className="text-xs text-secondary-500">{approval.role}</p>
                  <p className="mt-1 text-xs font-semibold text-primary-700">{approval.statusLabel}</p>
                </li>
              ))
            )}
          </ul>
        ) : null}
        {activeTab === 'history' ? <HistoryList items={change.history} /> : null}
      </div>
    </DraggableDetailPanel>
  );
}

function DrawerHeader({
  title,
  badge,
  onClose,
}: {
  title: string;
  badge?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-4">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-secondary-900">{title}</h3>
        {badge ? <div className="mt-2">{badge}</div> : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
        aria-label="Fechar detalhes"
      >
        <XMarkIcon className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

function DrawerTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: ReadonlyArray<readonly [string, string]>;
  active: string;
  onChange: (tab: string) => void;
}) {
  return (
    <div className="border-b border-secondary-200 px-2">
      <nav className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`shrink-0 rounded-none border-b-2 px-3 py-2.5 text-sm font-medium ${
              active === id
                ? 'border-primary-500 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-secondary-500">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ImpactBoxes({
  schedule,
  scope,
  cost,
}: {
  schedule: string;
  scope: string;
  cost: string;
}) {
  return (
    <section>
      <p className="text-sm font-semibold text-secondary-900">Impacto estimado</p>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <ImpactBox icon={<CalendarDaysIcon className="h-4 w-4" aria-hidden />} label="Prazo" value={schedule} />
        <ImpactBox icon={<FolderIcon className="h-4 w-4" aria-hidden />} label="Escopo" value={scope} />
        <ImpactBox icon={<BanknotesIcon className="h-4 w-4" aria-hidden />} label="Custo" value={cost} />
      </div>
    </section>
  );
}

function ImpactBox({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-primary-100 bg-primary-50/50 px-2 py-2 text-center">
      <div className="mx-auto flex h-7 w-7 items-center justify-center rounded-full bg-white text-primary-700">
        {icon}
      </div>
      <p className="mt-1 text-[10px] font-medium text-secondary-500">{label}</p>
      <p className="text-xs font-semibold text-secondary-900">{value}</p>
    </div>
  );
}

function HistoryList({
  items,
}: {
  items: { id: string; title: string; detail: string; whenLabel: string }[];
}) {
  if (items.length === 0) {
    return <p className="text-sm text-secondary-500">Nenhum histórico registrado.</p>;
  }
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.id} className="rounded-xl border border-secondary-200 px-3 py-2.5">
          <p className="text-sm font-semibold text-secondary-900">{item.title}</p>
          <p className="mt-0.5 text-sm text-secondary-600">{item.detail}</p>
          <p className="mt-1 text-xs text-secondary-400">{item.whenLabel}</p>
        </li>
      ))}
    </ul>
  );
}
